import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../../lib/api";
import type { CurrentUser } from "../../../lib/auth";
import { DialogPanel } from "../../../components/shared/DialogPanel";
import { Markdown } from "../../../components/wiki/Markdown";
import { WikiEditor, type WikiDraft } from "../../../components/wiki/Editor";
import { backdropClose } from "../../../components/shared/backdrop";

interface WikiArticle {
  id: string;
  title: string;
  body_md: string;
  tags: string[];
  author_id: string | null;
  author_name: string | null;
  created_at: string;
  updated_at: string;
}

interface WikiRevision {
  id: string;
  title: string;
  editor_name: string | null;
  created_at: string;
}

export const Route = createFileRoute("/_authed/wiki/$articleId")({
  component: WikiArticlePage,
});

const btnGhost: React.CSSProperties = {
  padding: "7px 14px", background: "var(--surface-input)", border: "1px solid var(--line-strong)",
  borderRadius: 8, color: "var(--ink-secondary)", fontSize: 13, cursor: "pointer",
};

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString("pt-BR", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

function WikiArticlePage() {
  const { articleId } = Route.useParams();
  const context = Route.useRouteContext();
  const user = (context as { user?: CurrentUser }).user;
  const navigate = useNavigate();
  const qc = useQueryClient();

  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<WikiDraft>({ title: "", body_md: "", tags: [] });
  const [showHistory, setShowHistory] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<WikiRevision | null>(null);
  // Exclusão em 2 etapas: 1º popup confirma intenção, 2º confirma de novo.
  const [deleteStep, setDeleteStep] = useState<0 | 1 | 2>(0);

  const { data: article, isLoading } = useQuery<WikiArticle>({
    queryKey: ["wiki-article", articleId],
    queryFn: () => api.get(`/wiki/${articleId}`),
  });

  const isAdmin = user?.role_name === "Admin";
  const canManage = !!article && (isAdmin || article.author_id === user?.user_id);

  const { data: revisions = [] } = useQuery<WikiRevision[]>({
    queryKey: ["wiki-revisions", articleId],
    queryFn: () => api.get(`/wiki/${articleId}/revisions`),
    enabled: showHistory && canManage,
  });

  function invalidate() {
    qc.invalidateQueries({ queryKey: ["wiki-article", articleId] });
    qc.invalidateQueries({ queryKey: ["wiki-articles"] });
    qc.invalidateQueries({ queryKey: ["wiki-tags"] });
    qc.invalidateQueries({ queryKey: ["wiki-revisions", articleId] });
  }

  const saveMut = useMutation({
    mutationFn: () => api.patch(`/wiki/${articleId}`, draft),
    onSuccess: () => { invalidate(); setEditing(false); },
  });

  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/wiki/${articleId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["wiki-articles"] });
      navigate({ to: "/wiki" });
    },
  });

  const restoreMut = useMutation({
    mutationFn: (revisionId: string) => api.post(`/wiki/${articleId}/revisions/${revisionId}/restore`, {}),
    onSuccess: () => { invalidate(); setRestoreTarget(null); setShowHistory(false); },
  });

  if (isLoading) {
    return <div style={{ padding: 32, fontSize: 13, color: "var(--ink-muted)" }}>Carregando...</div>;
  }
  if (!article) {
    return (
      <div style={{ padding: 32, fontSize: 13, color: "var(--ink-muted)" }}>
        Artigo não encontrado. <Link to="/wiki" style={{ color: "var(--brand)" }}>Voltar à Wiki</Link>
      </div>
    );
  }

  return (
    <div style={{ flex: 1, overflowY: "auto", padding: "24px 32px" }}>
      <div style={{ maxWidth: 860, margin: "0 auto", display: "flex", flexDirection: "column", gap: 18 }}>
        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
          <Link to="/wiki" style={{ fontSize: 13, color: "var(--ink-muted)", textDecoration: "none" }}>← Wiki</Link>
          <div style={{ display: "flex", gap: 8 }}>
            {editing ? (
              <>
                <button onClick={() => setEditing(false)} style={btnGhost}>Cancelar</button>
                <button
                  onClick={() => saveMut.mutate()}
                  disabled={!draft.title.trim() || saveMut.isPending}
                  style={{ padding: "7px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: !draft.title.trim() || saveMut.isPending ? 0.5 : 1 }}
                >
                  {saveMut.isPending ? "Salvando..." : "Salvar"}
                </button>
              </>
            ) : (
              <>
                {canManage && (
                  <button onClick={() => setShowHistory(true)} style={btnGhost}>Histórico</button>
                )}
                <button
                  onClick={() => { setDraft({ title: article.title, body_md: article.body_md, tags: article.tags }); setEditing(true); }}
                  style={btnGhost}
                >
                  Editar
                </button>
                {canManage && (
                  <button onClick={() => setDeleteStep(1)} style={{ ...btnGhost, color: "var(--danger)" }}>Excluir</button>
                )}
              </>
            )}
          </div>
        </div>

        {saveMut.isError && (
          <span style={{ fontSize: 12, color: "var(--danger)" }}>Falha ao salvar. Tente novamente.</span>
        )}

        {editing ? (
          <WikiEditor draft={draft} onChange={setDraft} />
        ) : (
          <>
            <div>
              <h1 style={{ fontSize: 24, fontWeight: 700, margin: "0 0 8px" }}>{article.title}</h1>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12, color: "var(--ink-muted)" }}>
                <span>{article.author_name ?? "—"}</span>
                <span>·</span>
                <span>atualizado em {fmtDateTime(article.updated_at)}</span>
                {article.tags.map(t => (
                  <span key={t} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "var(--brand-soft)", color: "var(--brand)" }}>
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <Markdown source={article.body_md} />
          </>
        )}
      </div>

      {/* Histórico */}
      {showHistory && (
        <div {...backdropClose(() => { setShowHistory(false); })} style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => setShowHistory(false)} label="Histórico de edições" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 520, maxHeight: "70vh", overflowY: "auto", display: "flex", flexDirection: "column", gap: 14 }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>Histórico de edições</span>
            {revisions.length === 0 && (
              <span style={{ fontSize: 13, color: "var(--ink-muted)" }}>Nenhuma edição anterior — este é o conteúdo original.</span>
            )}
            {revisions.map(r => (
              <div key={r.id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, padding: "10px 12px", border: "1px solid var(--line)", borderRadius: 8 }}>
                <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                  <span style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</span>
                  <span style={{ fontSize: 11, color: "var(--ink-muted)" }}>
                    {r.editor_name ?? "—"} · {fmtDateTime(r.created_at)}
                  </span>
                </div>
                <button onClick={() => setRestoreTarget(r)} style={{ ...btnGhost, padding: "5px 12px", fontSize: 12, flexShrink: 0 }}>
                  Restaurar
                </button>
              </div>
            ))}
          </DialogPanel>
        </div>
      )}

      {/* Confirmar restauração */}
      {restoreTarget && (
        <div style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => setRestoreTarget(null)} role="alertdialog" label="Restaurar revisão" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 420, display: "flex", flexDirection: "column", gap: 16 }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>Restaurar esta versão?</span>
            <p style={{ fontSize: 13, color: "var(--ink-secondary)", margin: 0 }}>
              O artigo voltará para a versão de <strong>{fmtDateTime(restoreTarget.created_at)}</strong>.
              O conteúdo atual será guardado no histórico — nada se perde.
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setRestoreTarget(null)} style={btnGhost}>Cancelar</button>
              <button
                onClick={() => restoreMut.mutate(restoreTarget.id)}
                disabled={restoreMut.isPending}
                style={{ padding: "7px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: restoreMut.isPending ? 0.6 : 1 }}
              >
                {restoreMut.isPending ? "Restaurando..." : "Restaurar"}
              </button>
            </div>
          </DialogPanel>
        </div>
      )}

      {/* Exclusão — confirmação dupla */}
      {deleteStep > 0 && (
        <div style={{ position: "fixed", inset: 0, background: "var(--overlay)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: "var(--z-modal)" as any }}>
          <DialogPanel onClose={() => setDeleteStep(0)} role="alertdialog" label="Excluir artigo" style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, padding: 24, width: 420, display: "flex", flexDirection: "column", gap: 16 }}>
            <span style={{ fontSize: 15, fontWeight: 600 }}>
              {deleteStep === 1 ? "Excluir artigo" : "Tem certeza absoluta?"}
            </span>
            <p style={{ fontSize: 13, color: "var(--ink-secondary)", margin: 0 }}>
              {deleteStep === 1 ? (
                <>Excluir <strong style={{ color: "var(--brand)" }}>{article.title}</strong>? Esta ação não pode ser desfeita pela interface.</>
              ) : (
                <>Última confirmação: o artigo e todo o seu histórico de edições serão apagados <strong>permanentemente</strong>.</>
              )}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setDeleteStep(0)} style={btnGhost}>Cancelar</button>
              <button
                onClick={() => (deleteStep === 1 ? setDeleteStep(2) : deleteMut.mutate())}
                disabled={deleteMut.isPending}
                style={{ padding: "7px 16px", background: "var(--danger)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: deleteMut.isPending ? 0.6 : 1 }}
              >
                {deleteStep === 1 ? "Excluir" : deleteMut.isPending ? "Excluindo..." : "Sim, excluir de vez"}
              </button>
            </div>
          </DialogPanel>
        </div>
      )}
    </div>
  );
}
